import { useState, useMemo } from 'react'
import { AutoComplete, Spin } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { debounce } from 'lodash-es'
import { hospitalsApi } from '@/api/hospitals'
import type { Hospital } from '@/types/hospital'

interface HospitalSearchProps {
  onSelect: (hospital: Hospital) => void
  placeholder?: string
  disabled?: boolean
  excludeIds?: string[]
}

export const HospitalSearch = ({
  onSelect,
  placeholder = 'Search hospitals...',
  disabled = false,
  excludeIds = [],
}: HospitalSearchProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Debounced search query update
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value)
      }, 300),
    []
  )

  // Fetch hospitals based on search query
  const { data, isLoading } = useQuery({
    queryKey: ['hospitals-search', searchQuery],
    queryFn: () => hospitalsApi.searchHospitals(searchQuery, 10),
    enabled: searchQuery.length >= 2,
  })

  const handleSearch = (value: string) => {
    setSearchValue(value)
    if (value.length >= 2) {
      debouncedSearch(value)
    }
  }

  const handleSelect = (_value: string, option: { hospital: Hospital }) => {
    onSelect(option.hospital)
    setSearchValue('')
    setSearchQuery('')
  }

  // Filter out excluded hospitals
  const filteredHospitals = useMemo(() => {
    if (!data?.data) return []
    return data.data.filter((h) => !excludeIds.includes(h.id))
  }, [data?.data, excludeIds])

  // Transform hospitals into AutoComplete options
  const options = filteredHospitals.map((hospital) => ({
    value: hospital.id,
    label: (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <strong>{hospital.name}</strong>
        <span style={{ fontSize: '12px', color: '#888' }}>
          {hospital.city}, {hospital.state}
          {hospital.cmsid && ` • CMS ID: ${hospital.cmsid}`}
        </span>
      </div>
    ),
    hospital,
  }))

  return (
    <AutoComplete
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%' }}
      notFoundContent={
        isLoading ? (
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        ) : searchQuery.length >= 2 ? (
          <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
            No hospitals found
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
            Type at least 2 characters to search
          </div>
        )
      }
    />
  )
}
